/**
 * EmotePicker (spec §4.4, DESIGN-client §7): 10 equipped slots, 3s cooldown ring, sends the
 * `emote` intent. Long-press (or the explicit button) on another seat's avatar toggles
 * "Mute emotes from X" in `settings.mutedEmotes`.
 */
import { useEffect, useState } from 'react';
import { useStore } from '../../store/store';
import { EMOTE_COOLDOWN_MS, EmoteCooldown, emoteById, toggleMuted } from '../emotes';
import { seatDisplayName, teamOfSeat } from '../helpers';
import { intents } from '../runtime';
import { Avatar, Button, Sheet, toast } from '../ui';

export const emoteCooldown = new EmoteCooldown(EMOTE_COOLDOWN_MS);

export function EmotePicker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const slots = useStore((s) => s.settings.emoteSlots);
  const mutedList = useStore((s) => s.settings.mutedEmotes);
  const setSettings = useStore((s) => s.setSettings);
  const table = useStore((s) => s.table);
  const me = useStore((s) => s.session.me);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!open) return;
    const iv = setInterval(() => tick((n) => n + 1), 100);
    return () => clearInterval(iv);
  }, [open]);

  const progress = emoteCooldown.progress();
  const ready = emoteCooldown.ready();

  const send = (id: string) => {
    if (!emoteCooldown.fire()) {
      toast(`Easy… ${Math.ceil(emoteCooldown.remaining() / 1000)}s`, 'info', 900);
      return;
    }
    intents()?.emote(id);
    onClose();
  };

  const others = (table?.seats ?? [])
    .map((s, i) => ({ seat: s, i }))
    .filter(({ seat }) => seat.controller !== 'empty' && seat.playerId && seat.playerId !== me?.id);

  return (
    <Sheet open={open} title="Emotes" onClose={onClose} testId="emote-picker">
      <div className="stack">
        <div className="emotes" data-testid="emote-grid">
          {slots.slice(0, 10).map((id) => {
            const def = emoteById(id);
            if (!def) return null;
            return (
              <button key={id} type="button" className="emote" disabled={!ready} aria-label={def.label} data-testid={`emote-${id}`} onClick={() => send(id)}>
                <span aria-hidden>{def.glyph}</span>
                <span className="emote__label">{def.label}</span>
                {!ready && (
                  <svg className="cooldown-ring" viewBox="0 0 36 36" aria-hidden>
                    <circle cx="18" cy="18" r="16" fill="none" stroke="var(--slate-deep)" strokeWidth="3" />
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="var(--orange)"
                      strokeWidth="3"
                      strokeDasharray={`${progress * 100.5} 100.5`}
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
        {others.length > 0 && (
          <div className="stack">
            <div className="eyebrow">Mute emotes from</div>
            {others.map(({ seat, i }) => {
              const isMuted = mutedList.includes(seat.playerId);
              return (
                <div key={i} className="row row--between">
                  <div className="row">
                    <Avatar
                      name={seatDisplayName(seat)}
                      src={seat.avatar}
                      size={36}
                      team={teamOfSeat(i)}
                      bot={seat.controller === 'bot'}
                      muted={isMuted}
                      onLongPress={() => setSettings({ mutedEmotes: toggleMuted(mutedList, seat.playerId) })}
                    />
                    <span>{seatDisplayName(seat)}</span>
                  </div>
                  <Button
                    variant={isMuted ? 'primary' : 'ghost'}
                    testId={`mute-${i}`}
                    aria-pressed={isMuted}
                    onClick={() => setSettings({ mutedEmotes: toggleMuted(mutedList, seat.playerId) })}
                  >
                    {isMuted ? 'Unmute' : 'Mute'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Sheet>
  );
}
